import React from 'react';
import { fileUrl, sortByPoints } from '../gameUtils';

const HEADERS = [
  ['PTS'], ['OR', 'Offensive rebounds'], ['DR', 'Defensive rebounds'], ['REB'], ['AST'],
  ['STL'], ['BLK'], ['TO'], ['FG'], ['3P'], ['FT'], ['+/-', 'Plus / minus while on court'],
];

const pmClass = (pm) => (pm > 0 ? 'text-emerald-300' : pm < 0 ? 'text-rose-300' : 'text-white/50');
const pmText = (pm) => (pm > 0 ? `+${pm}` : `${pm}`);

function PlayerRow({ p }) {
  const dnp = (p.pts + p.reb + p.ast + p.stl + p.blk + p.to + p.fga + p.fta + p.foul) === 0;
  const pm = p.plus_minus ?? 0;
  return (
    <tr className={`border-t border-white/5 ${dnp ? 'opacity-40' : ''}`}>
      <td className="py-1.5 px-2 text-white">
        <span className="text-amber-300 italic font-bold mr-1.5">#{p.jersey || '?'}</span>
        {p.name}
      </td>
      <td className="text-center font-bold text-white">{p.pts}</td>
      <td className="text-center text-white/70">{p.oreb}</td>
      <td className="text-center text-white/70">{p.dreb}</td>
      <td className="text-center text-white/85 font-semibold">{p.reb}</td>
      <td className="text-center text-white/80">{p.ast}</td>
      <td className="text-center text-white/70">{p.stl}</td>
      <td className="text-center text-white/70">{p.blk}</td>
      <td className="text-center text-rose-300/80">{p.to}</td>
      <td className="text-center text-white/60 tabular-nums">{p.fgm}-{p.fga}</td>
      <td className="text-center text-white/60 tabular-nums">{p.three_m}-{p.three_a}</td>
      <td className="text-center text-white/60 tabular-nums">{p.ftm}-{p.fta}</td>
      <td className={`text-center font-bold tabular-nums ${pmClass(pm)}`}>{pmText(pm)}</td>
    </tr>
  );
}

function TotalsRow({ totals }) {
  const pm = totals.plus_minus ?? 0;
  return (
    <tr className="border-t-2 border-amber-400/40 bg-white/5 font-bold">
      <td className="py-2 px-2 text-amber-300 uppercase tracking-wider text-[10px]">Totals</td>
      <td className="text-center text-amber-300">{totals.pts}</td>
      <td className="text-center text-white/85">{totals.oreb}</td>
      <td className="text-center text-white/85">{totals.dreb}</td>
      <td className="text-center text-white">{totals.reb}</td>
      <td className="text-center text-white">{totals.ast}</td>
      <td className="text-center text-white/80">{totals.stl}</td>
      <td className="text-center text-white/80">{totals.blk}</td>
      <td className="text-center text-rose-300/80">{totals.to}</td>
      <td className="text-center text-white/70 tabular-nums">{totals.fgm}-{totals.fga}</td>
      <td className="text-center text-white/70 tabular-nums">{totals.three_m}-{totals.three_a}</td>
      <td className="text-center text-white/70 tabular-nums">{totals.ftm}-{totals.fta}</td>
      <td className={`text-center tabular-nums ${pmClass(pm)}`}>{pm > 0 ? `+${pm}` : pm}</td>
    </tr>
  );
}

export function BoxScoreTeamTable({ team, rows, totals, teamKey }) {
  return (
    <div className="rounded-xl border border-white/10 overflow-hidden bg-[#0F1A4A]/70" data-testid={`bs-team-${teamKey}`}>
      <div className="bg-gradient-to-r from-[#001A5C] to-[#00247D] px-3 py-2 flex items-center gap-2 border-b border-white/10">
        {team.logo && (
          <img src={fileUrl(team.logo)} alt="" className="w-7 h-7 rounded-full bg-white object-cover border border-white/20" />
        )}
        <div className="flex-1 text-white font-bold text-sm sm:text-base truncate">{team.name}</div>
        <div className="text-amber-300 font-black text-lg tabular-nums">{totals.pts}</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px] sm:text-xs">
          <thead className="bg-white/5 text-white/50 uppercase tracking-wider">
            <tr>
              <th className="text-left py-1.5 px-2 font-semibold">Player</th>
              {HEADERS.map(([label, title]) => (
                <th key={label} className="px-1.5 font-semibold" title={title}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortByPoints(rows).map(p => <PlayerRow key={p.player_id} p={p} />)}
            <TotalsRow totals={totals} />
          </tbody>
        </table>
      </div>
    </div>
  );
}
